import { PageLink } from '../../../../../../_metronic/layout/core'
import { ROLE } from '../../../../auth'

export type CommissionDirection =
  | 'PAYIN_MERCHANT'
  | 'PAYIN_CASSA'
  | 'PAYOUT_MERCHANT'
  | 'PAYOUT_CASSA'
  | 'MATCHING_PAYOUT'
  | 'MATCHING_PAYIN'

export type CommissionCurrency = 'RUB' | 'USDT'
export type CashboxKind = 'COUNTERPARTY' | 'FARM' | 'MK'

export const DIRECTION_LABELS: Record<CommissionDirection, string> = {
  PAYIN_MERCHANT:      'Pay-in мерчант',
  PAYIN_CASSA:         'Pay-in касса',
  PAYOUT_MERCHANT:     'Pay-out мерчант',
  PAYOUT_CASSA:        'Pay-out касса',
  MATCHING_PAYOUT:     'Matching pay-out',
  MATCHING_PAYIN:      'Matching pay-in',
}

export const CURRENCY_LABELS: Record<CommissionCurrency, string> = {
  RUB:  'RUB',
  USDT: 'USDT',
}

export const KIND_LABELS: Record<CashboxKind, string> = {
  COUNTERPARTY: 'Касса-контрагент',
  FARM:         'Ферма',
  MK:           'Массовый кэш',
}

/**
 * Знак правила комиссии (CPAYMENT-1681). Ставка (bps) всегда положительная,
 * направление денег задаёт это поле.
 */
export type CommissionFeeKind = 'CHARGE' | 'REBATE'

export const FEE_KIND_LABELS: Record<CommissionFeeKind, string> = {
  CHARGE: 'Мерчант → PS (комиссия)',
  REBATE: 'PS → мерчант (ребейт)',
}

export const FEE_KIND_OPTIONS: { value: CommissionFeeKind; label: string }[] =
  (Object.keys(FEE_KIND_LABELS) as CommissionFeeKind[]).map((value) => ({
    value,
    label: FEE_KIND_LABELS[value],
  }))

/**
 * Ребейт поддержан только мерчантскими направлениями — зеркалит гейт ledger
 * (Direction.RebateAllowed) и валидатор бэкенда. Для кассовых направлений
 * селект залочен на CHARGE: «касса платит PS» — другое бизнес-соглашение,
 * оно не спроектировано.
 */
export const REBATE_ALLOWED_DIRECTIONS: CommissionDirection[] = [
  'PAYIN_MERCHANT',
  'PAYOUT_MERCHANT',
  'MATCHING_PAYOUT',
  'MATCHING_PAYIN',
]

/** Процент правила со знаком: `+1.00%` для комиссии, `−1.00%` для ребейта. */
export const formatRuleBps = (bps: number, feeKind: CommissionFeeKind): string =>
  `${feeKind === 'REBATE' ? '−' : '+'}${(bps / 100).toFixed(2)}%`

/**
 * Состояние поля «Кто платит комиссию» в форме правила.
 *
 * `forcedValue` — значение, которое форма обязана проставить сама;
 * `null` означает «выбирает оператор».
 */
export interface FeeKindFieldState {
  disabled:    boolean
  forcedValue: CommissionFeeKind | null
  hint:        string
}

/**
 * У поля три состояния, а не два. Схлопывание первых двух в «ребейт недоступен»
 * оставляло кассовое направление с **пустым** значением при обязательном поле —
 * и правило кассы становилось невозможно создать, без указания виновного поля
 * (CPAYMENT-1681). «Залочено» обязано означать «зафиксировано на CHARGE».
 */
export const feeKindFieldState = (direction: string): FeeKindFieldState => {
  if (direction === '') {
    return {
      disabled:    true,
      forcedValue: null,
      hint:        'Сначала выберите направление',
    }
  }
  if (!REBATE_ALLOWED_DIRECTIONS.includes(direction as CommissionDirection)) {
    return {
      disabled:    true,
      forcedValue: 'CHARGE',
      hint:        'Для кассовых направлений доступна только комиссия с мерчанта — ребейт для касс не поддержан',
    }
  }
  return {
    disabled:    false,
    forcedValue: null,
    hint:        '«Ребейт» — PS выплачивает мерчанту вместо списания. Процент задаётся положительным числом, знак берётся отсюда',
  }
}

export const COMMISSION_RULES_BREADCRUMBS: PageLink[] = [
  {
    title:       'Комиссии',
    path:        '/settings/commission-rules',
    isSeparator: false,
    isActive:    true,
    roles:       [ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER],
  },
]

export const MONEY_PATTERN = /^\d{1,9}(\.\d{1,4})?$/
